import type { SnackbarKey } from 'notistack';

import { Box, Collapse, IconButton } from '@mui/material';
// @mui
import { alpha } from '@mui/material/styles';
import { SnackbarProvider as NotistackProvider } from 'notistack';
import { useRef } from 'react';

import type { IconifyProps } from '../iconify';

import Iconify from '../iconify';
//
import { useSettingsContext } from '../settings';
//
import StyledNotistack from './styles';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

const SnackbarProvider = ({ children }: Props) => {
  const { themeDirection } = useSettingsContext();

  const isRTL = themeDirection === 'rtl';

  const notistackRef = useRef<any>(null);

  const onClose = (key: SnackbarKey) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <>
      <StyledNotistack />

      <NotistackProvider
        action={(key) => (
          <IconButton onClick={onClose(key)} size="small" sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        )}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        autoHideDuration={3000}
        dense
        iconVariant={{
          error: <SnackbarIcon color="error" icon="eva:alert-circle-fill" />,
          info: <SnackbarIcon color="info" icon="eva:info-fill" />,
          success: (
            <SnackbarIcon color="success" icon="eva:checkmark-circle-2-fill" />
          ),
          warning: (
            <SnackbarIcon color="warning" icon="eva:alert-triangle-fill" />
          ),
        }}
        maxSnack={20}
        preventDuplicate
        ref={notistackRef}
        TransitionComponent={isRTL ? Collapse : undefined}
        // With close as default
        variant="success" // Set default variant
      >
        {children}
      </NotistackProvider>
    </>
  );
};

export default SnackbarProvider;

// ----------------------------------------------------------------------

type SnackbarIconProps = {
  color: 'error' | 'info' | 'success' | 'warning';
  icon: IconifyProps;
};

const SnackbarIcon = ({ color, icon }: SnackbarIconProps) => {
  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16),
        borderRadius: 1.5,
        color: `${color}.main`,
        display: 'flex',
        height: 40,
        justifyContent: 'center',
        mr: 1.5,
        width: 40,
      }}
    >
      <Iconify icon={icon} width={24} />
    </Box>
  );
};
